import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select as ChakraSelect,
  Stack,
} from '@chakra-ui/react';
import { Select as RSelect } from 'chakra-react-select';
import { useQuery } from '@tanstack/react-query';

import { useFechamentos, IFiltrosOptions } from '../../../../../hooks/useFechamentos';

export interface IFechamentoFilters {
  uf?: string;
  cidade?: string;
  tipo_regime?: string;
  grupo_de_empresas?: string;
  apenas_ativas: boolean;

  // Responsaveis
  comercial?: string;
  contabil?: string;
  fiscal?: string;
  pessoal?: string;

  // Outros
  tags?: string;
  dt_cli_desde?: string;
  dt_cli_ate?: string;
}

export const DEFAULT_FILTERS: IFechamentoFilters = {
  uf: '',
  cidade: '',
  tipo_regime: '',
  grupo_de_empresas: '',
  apenas_ativas: false,
  comercial: '',
  contabil: '',
  fiscal: '',
  pessoal: '',
  tags: '',
  dt_cli_desde: '',
  dt_cli_ate: '',
};

const REGIME_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'Simples Nacional', label: 'Simples Nacional' },
  { value: 'Lucro Presumido', label: 'Lucro Presumido' },
  { value: 'Lucro Real', label: 'Lucro Real' },
  { value: 'MEI', label: 'MEI' },
];

interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: IFechamentoFilters;
  // eslint-disable-next-line no-unused-vars
  onApply: (filters: IFechamentoFilters) => void;
}

interface RSOption {
  value: string;
  label: string;
}

const toOptions = (arr: string[] | undefined): RSOption[] =>
  (arr ?? []).map(v => ({ value: v, label: v }));

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Heading
    as="h4"
    fontSize="11px"
    color="gray.500"
    textTransform="uppercase"
    letterSpacing="0.06em"
    fontWeight="bold"
    mt={3}
    mb={2}
  >
    {children}
  </Heading>
);

export const FiltersDrawer = ({ isOpen, onClose, filters, onApply }: FiltersDrawerProps) => {
  const { getFiltrosOptions } = useFechamentos();
  const [draft, setDraft] = useState<IFechamentoFilters>(filters);

  // Reset draft quando abre o drawer
  useEffect(() => {
    if (isOpen) setDraft(filters);
  }, [isOpen, filters]);

  // Carrega opcoes de autocomplete (1x ao primeiro abrir, fica em cache)
  const { data: optionsData } = useQuery({
    queryKey: ['fechamento-filtros-options'],
    queryFn: getFiltrosOptions,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // 5min
  });
  const options: IFiltrosOptions | undefined = optionsData?.data;

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  const handleClear = () => setDraft(DEFAULT_FILTERS);

  // Helpers pra simplificar autocomplete
  const setField = (field: keyof IFechamentoFilters, value: string) =>
    setDraft(d => ({ ...d, [field]: value }));

  const valueOption = (v?: string): RSOption | null => (v ? { value: v, label: v } : null);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">Filtros</DrawerHeader>

        <DrawerBody>
          <Stack spacing={3} mt={1}>
            {/* === Localizacao === */}
            <SectionTitle>Localização</SectionTitle>

            <FormControl>
              <FormLabel fontSize="sm">UF</FormLabel>
              <RSelect
                size="sm"
                placeholder="Selecione um estado..."
                isClearable
                options={toOptions(options?.ufs)}
                value={valueOption(draft.uf)}
                onChange={opt => setField('uf', opt?.value ?? '')}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Cidade</FormLabel>
              <RSelect
                size="sm"
                placeholder="Selecione uma cidade..."
                isClearable
                options={toOptions(options?.cidades)}
                value={valueOption(draft.cidade)}
                onChange={opt => setField('cidade', opt?.value ?? '')}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Grupo de empresas</FormLabel>
              <RSelect
                size="sm"
                placeholder="Selecione um grupo..."
                isClearable
                options={toOptions(options?.grupos)}
                value={valueOption(draft.grupo_de_empresas)}
                onChange={opt => setField('grupo_de_empresas', opt?.value ?? '')}
              />
            </FormControl>

            {/* === Tributacao === */}
            <SectionTitle>Tributação</SectionTitle>

            <FormControl>
              <FormLabel fontSize="sm">Regime tributário</FormLabel>
              <ChakraSelect
                size="sm"
                value={draft.tipo_regime || ''}
                onChange={e => setField('tipo_regime', e.target.value)}
              >
                {REGIME_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </ChakraSelect>
            </FormControl>

            {/* === Estado === */}
            <SectionTitle>Estado</SectionTitle>

            <FormControl>
              <Checkbox
                isChecked={draft.apenas_ativas}
                onChange={e => setDraft(d => ({ ...d, apenas_ativas: e.target.checked }))}
              >
                Apenas empresas ativas
              </Checkbox>
            </FormControl>

            {/* === Responsaveis === */}
            <SectionTitle>Responsáveis</SectionTitle>

            <FormControl>
              <FormLabel fontSize="sm">Comercial</FormLabel>
              <RSelect
                size="sm"
                placeholder="Responsável comercial..."
                isClearable
                options={toOptions(options?.comercial)}
                value={valueOption(draft.comercial)}
                onChange={opt => setField('comercial', opt?.value ?? '')}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Contábil</FormLabel>
              <RSelect
                size="sm"
                placeholder="Responsável contábil..."
                isClearable
                options={toOptions(options?.contabil)}
                value={valueOption(draft.contabil)}
                onChange={opt => setField('contabil', opt?.value ?? '')}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Fiscal</FormLabel>
              <RSelect
                size="sm"
                placeholder="Responsável fiscal..."
                isClearable
                options={toOptions(options?.fiscal)}
                value={valueOption(draft.fiscal)}
                onChange={opt => setField('fiscal', opt?.value ?? '')}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Pessoal / DP</FormLabel>
              <RSelect
                size="sm"
                placeholder="Responsável pessoal..."
                isClearable
                options={toOptions(options?.pessoal)}
                value={valueOption(draft.pessoal)}
                onChange={opt => setField('pessoal', opt?.value ?? '')}
              />
            </FormControl>

            {/* === Outros === */}
            <SectionTitle>Outros</SectionTitle>

            <FormControl>
              <FormLabel fontSize="sm">Tags</FormLabel>
              <Input
                size="sm"
                placeholder="busca por substring (ex: holding)"
                value={draft.tags || ''}
                onChange={e => setField('tags', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Cliente desde (a partir de)</FormLabel>
              <Input
                size="sm"
                type="date"
                value={draft.dt_cli_desde || ''}
                onChange={e => setField('dt_cli_desde', e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Cliente até</FormLabel>
              <Input
                size="sm"
                type="date"
                value={draft.dt_cli_ate || ''}
                onChange={e => setField('dt_cli_ate', e.target.value)}
              />
            </FormControl>

            <Box h={2} />
          </Stack>
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px">
          <Flex justify="space-between" w="full">
            <Button variant="ghost" size="sm" onClick={handleClear}>
              Limpar
            </Button>
            <Flex gap={2}>
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme="blue" size="sm" onClick={handleApply}>
                Aplicar
              </Button>
            </Flex>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

/**
 * Converte IFechamentoFilters para o formato aceito pelo backend
 * (omite chaves vazias para nao poluir a query string).
 */
export const filtersToParams = (f: IFechamentoFilters): Record<string, string> => {
  const out: Record<string, string> = {};
  if (f.uf) out.uf = f.uf;
  if (f.cidade) out.cidade = f.cidade;
  if (f.tipo_regime) out.tipo_regime = f.tipo_regime;
  if (f.grupo_de_empresas) out.grupo_de_empresas = f.grupo_de_empresas;
  if (f.apenas_ativas) out.ativa = 'Ativa';
  if (f.comercial) out.comercial = f.comercial;
  if (f.contabil) out.contabil = f.contabil;
  if (f.fiscal) out.fiscal = f.fiscal;
  if (f.pessoal) out.pessoal = f.pessoal;
  if (f.tags) out.tags = f.tags;
  if (f.dt_cli_desde) out.dt_cli_desde = f.dt_cli_desde;
  if (f.dt_cli_ate) out.dt_cli_ate = f.dt_cli_ate;
  return out;
};

/** Conta quantos filtros estao ativos. */
export const countActiveFilters = (f: IFechamentoFilters): number => {
  let n = 0;
  if (f.uf) n += 1;
  if (f.cidade) n += 1;
  if (f.tipo_regime) n += 1;
  if (f.grupo_de_empresas) n += 1;
  if (f.apenas_ativas) n += 1;
  if (f.comercial) n += 1;
  if (f.contabil) n += 1;
  if (f.fiscal) n += 1;
  if (f.pessoal) n += 1;
  if (f.tags) n += 1;
  if (f.dt_cli_desde) n += 1;
  if (f.dt_cli_ate) n += 1;
  return n;
};
